import React, { useCallback, useEffect, useState } from "react";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { money, pct } from "../../utils/format";

type DRELine = {
  lineType: string;
  description: string;
  amount: number;
  percentage: number | null;
};

type DREReport = {
  competenceYear: number;
  competenceMonth: number;
  lines: DRELine[];
  indicators: {
    grossMargin: number | null;
    operatingMargin: number | null;
    netMargin: number | null;
  };
};

const monthLabels = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export const DREPage: React.FC = () => {
  const { client } = useAuth();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [data, setData] = useState<DREReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ startYear: year, startMonth: month, endYear: year, endMonth: month });
    const response = await client.get(`/reports/financial/dre?${params.toString()}`);
    if (!response.ok) {
      setError("Falha ao carregar DRE.");
      setLoading(false);
      return;
    }
    const payload = (await response.json()) as { data?: DREReport } | DREReport;
    const report = "data" in payload ? payload.data : payload;
    setData(report ?? null);
    setLoading(false);
  }, [client, year, month]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  const indicators = data?.indicators ?? { grossMargin: null, operatingMargin: null, netMargin: null };

  const marginColor = (v: number | null) => {
    if (v === null) return "#6c757d";
    if (v >= 0.2) return "#198754";
    if (v >= 0) return "#fd7e14";
    return "#dc3545";
  };

  return (
    <div className="container py-4">
      <h1 className="mb-3">DRE</h1>

      <div className="card shadow-sm mb-3" style={{ borderTop: "3px solid #0d6efd" }}>
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label form-label-sm mb-1">Ano</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label form-label-sm mb-1">Mes</label>
              <select
                className="form-select form-select-sm"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {monthLabels.map((label, index) => (
                  <option key={index + 1} value={index + 1}>{label}</option>
                ))}
              </select>
            </div>
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-primary btn-sm" onClick={loadReport}>Buscar</button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <PageState loading={loading} error={error}>
        {data && (
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card shadow-sm h-100" style={{ borderLeft: `4px solid ${marginColor(indicators.grossMargin)}` }}>
                  <div className="card-body">
                    <div className="text-muted small">Margem Bruta</div>
                    <div className="fw-bold fs-4 mt-1">{pct(indicators.grossMargin)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm h-100" style={{ borderLeft: `4px solid ${marginColor(indicators.operatingMargin)}` }}>
                  <div className="card-body">
                    <div className="text-muted small">Margem Operacional</div>
                    <div className="fw-bold fs-4 mt-1">{pct(indicators.operatingMargin)}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card shadow-sm h-100" style={{ borderLeft: `4px solid ${marginColor(indicators.netMargin)}` }}>
                  <div className="card-body">
                    <div className="text-muted small">Margem Liquida</div>
                    <div className="fw-bold fs-4 mt-1">{pct(indicators.netMargin)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="mb-3">
                  {monthLabels[(data.competenceMonth || 1) - 1]} / {data.competenceYear}
                </h5>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "50%" }}>Conta</th>
                        <th className="text-end">Valor</th>
                        <th className="text-end">% Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.lines ?? []).map((line, index) => {
                        const isBold =
                          line.lineType.includes("TOTAL") ||
                          line.lineType.includes("RESULT") ||
                          line.lineType === "REVENUE";
                        return (
                          <tr key={index} className={isBold ? "fw-bold" : ""}>
                            <td className={line.lineType.startsWith("TOTAL") ? "ps-2" : "ps-4"}>
                              {line.description}
                            </td>
                            <td className="text-end">{money(Math.abs(line.amount))}</td>
                            <td className="text-end">{pct(line.percentage)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </PageState>
    </div>
  );
};
